import React, {useEffect} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useCustomPreferenceWithLocale} from '../../../../hooks/use-custom-preference'
import {CalendarIcon} from '../../../../components/icons'

/**
 * A React hook that provides the field definitions for the contact us form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.formState.errors - An object containing field errors
 * @returns {Object} Field definitions for use in a form
 */
export default function useContactUsFields({
    form: {
        control,
        formState: {errors},
        watch, // Use watch to observe changes to form values
        trigger,
        getFieldState,
    },
    prefix = '',
    variant = 'row',
}) {
    const {formatMessage} = useIntl()
    const contactUsConfiguration = useCustomPreferenceWithLocale('contactUsConfiguration') || {}
    // Helper to safely retrieve a label for a field.
    // If the field exists in fieldLabels, it uses the localized message.
    // Otherwise, it generates a human-readable fallback from the field name (e.g., "serialNumber" → "Serial Number").
    const getLabel = (field) =>
        fieldLabels[field]
            ? formatMessage(fieldLabels[field])
            : field.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())

    const fieldLabels = defineMessages({
        firstName: {
            id: 'use_address_fields.label.first_name',
            defaultMessage: 'First Name',
        },
        lastName: {
            id: 'use_address_fields.label.last_name',
            defaultMessage: 'Last Name',
        },
        productName: {
            id: 'product_item.link.product.label',
            defaultMessage: 'Product Name',
        },
        serialNumber: {
            id: 'contact_us.serialNumber.label',
            defaultMessage: 'Serial Number',
        },
        retailer: {
            id: 'contact_us.retailer.label',
            defaultMessage: 'Retailer',
        },
        orderNumber: {
            id: 'checkout_confirmation.label.order_number',
            defaultMessage: 'Order Number',
        },
        purchaseDate: {
            id: 'product_registration_fields.label.purchase_date',
            defaultMessage: 'Purchased on*',
        },
        claimNumber: {
            id: 'contact_us.claimNumber.label',
            defaultMessage: 'Claim Number',
        },
        uploadFile: {
            id: 'product_registration_fields.label.upload_file',
            defaultMessage: 'Choose file',
        },
        privacy: {
            id: 'contact_us.privacy.label',
            defaultMessage: 'I confirm that I have read and understood the Privacy Policy.',
        },
        message: {
            id: 'contact_us.freeText.label',
            defaultMessage: 'Message',
        },
        email: {
            id: 'profile_card.label.email',
            defaultMessage: 'Email',
        },
        emailConfirmation: {
            id: 'contact_us.emailConfirmation.label',
            defaultMessage: 'Email confirmation',
        },
        address: {
            id: 'account.heading.address',
            defaultMessage: 'Address',
        },
        phone: {
            id: 'use_address_fields.label.phone',
            defaultMessage: 'Phone',
        },
        freeText: {
            id: 'contact_us.freeText.label',
            defaultMessage: 'Message',
        },
        provinceRegion: {
            id: 'use_address_fields.label.province_region',
            defaultMessage: 'Province/Region',
        },
        country: {defaultMessage: 'Country', id: 'use_address_fields.label.country'},
        zipCode: {defaultMessage: 'Zip Code', id: 'use_address_fields.label.zipCode'},
    })

    const reasons = contactUsConfiguration?.reasons || {}
    const reasonDropdown = Object.keys(reasons).map((reason) => ({
        value: reason,
        label: reason,
    }))

    // Watch for changes
    const emailValue = watch(`${prefix}email`)
    const selectedReason = watch(`${prefix}requestType`)
    const reasonFields = reasons[selectedReason?.value] || []

    useEffect(() => {
        // Trigger validation for emailConfirmation when email changes
        const emailTouched = getFieldState(`${prefix}email`).isTouched
        if (emailTouched) {
            trigger(`${prefix}emailConfirmation`)
        }
    }, [emailValue])

    // Initialize dynamicFields with the requestType field
    const dynamicFields = {
        requestType: {
            name: `${prefix}requestType`,
            label: formatMessage({
                id: 'contact_us.request_kind.placeholder',
                defaultMessage: 'Request type',
            }),
            defaultValue: null,
            type: 'select',
            options: reasonDropdown,
            rules: {
                required: formatMessage({
                    id: 'use_contacts_us_fields.error.required',
                    defaultMessage: 'Mandatory field',
                }),
            },
            error: errors[`${prefix}requestType`],
            control,
        },
    }
    // Add fields dynamically based on the selected reason
    reasonFields.forEach(({field, mandatory, values, content}) => {
        if (field !== 'requestType') {
            // List of fields to mask
            const maskFields = [
                'firstName',
                'lastName',
                'email',
                'phone',
                'taxCode',
                'vatNumber',
                'dateOfBirth',
                'gender',
                'address',
                'provinceRegion',
                'country',
                'zipCode',
                'orderNumber',
                'claimNumber',
                'cardHolder',
                'cardNumber',
                'cardExpiry',
                'iban',
                'billingAddress',
                'piva',
                'sdi',
            ]
            dynamicFields[field] = {
                name: field === 'purchagedate' ? `${prefix}purchaseDate` : `${prefix}${field}`,
                label: getLabel(field),
                placeholder: ['freeText', 'purchaseDate'].includes(field)
                    ? getLabel(field)
                    : undefined,
                type:
                    field === 'purchaseDate'
                        ? 'date'
                        : field === 'uploadFile'
                        ? 'file'
                        : field === 'privacy'
                        ? 'checkbox'
                        : field === 'address'
                        ? 'address' // Special type for address
                        : field === 'productName' && values
                        ? 'select' // Make productName a select if values are provided
                        : field === 'freeText'
                        ? 'textarea' // freeText to a textarea field
                        : field === 'description' && content
                        ? 'html' // Handle description as an HTML field
                        : 'text',
                defaultValue: field === 'privacy' ? false : '',
                calendarIcon: field === 'purchaseDate' ? <CalendarIcon /> : undefined,
                options:
                    field === 'productName' && values
                        ? values.map((value) => ({value, label: value}))
                        : undefined, // Populate options for productName
                content: field === 'description' ? content : undefined, // Add content for description
                rules: mandatory
                    ? {
                          required: formatMessage({
                              id: 'use_contacts_us_fields.error.required',
                              defaultMessage: 'Mandatory field',
                          }),
                          ...(field === 'email' && {
                              pattern: {
                                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, // Basic email validation regex
                                  message: formatMessage({
                                      id: 'use_contacts_us_fields.error.invalid_email',
                                      defaultMessage: 'Invalid email address',
                                  }),
                              },
                          }),
                          ...(field === 'emailConfirmation' && {
                              validate: (value) =>
                                  value === emailValue ||
                                  formatMessage({
                                      id: 'use_contacts_us_fields.error.email_mismatch',
                                      defaultMessage: 'Email addresses do not match',
                                  }),
                          }),
                      }
                    : field === 'emailConfirmation'
                    ? {
                          validate: (value) =>
                              value === emailValue ||
                              formatMessage({
                                  id: 'use_contacts_us_fields.error.email_mismatch',
                                  defaultMessage: 'Email addresses do not match',
                              }),
                      }
                    : field === 'email'
                    ? {
                          pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, // Basic email validation regex
                              message: formatMessage({
                                  id: 'use_contacts_us_fields.error.invalid_email',
                                  defaultMessage: 'Invalid email address',
                              }),
                          },
                      }
                    : {},
                error: errors[`${prefix}${field}`],
                control,
                ...(field === 'uploadFile' && {
                    rules: {
                        validate: {
                            fileSize: (file) =>
                                !file ||
                                file.size <= 5 * 1024 * 1024 || // 5 MB limit
                                formatMessage({
                                    defaultMessage: 'File size should not exceed 5 MB.',
                                    id: 'product_registration_fields.error.file_size_exceeded',
                                }),
                        },
                    },
                }),
                ...(field === 'privacy' && {variant}), // Add variant for privacy field
                ...(maskFields.includes(field) && {dataCsMask: true}),
            }
        }
    })

    return dynamicFields
}
